import { Button } from '@/components/ui/button';
import { useCandidate, useCandidateFiles } from '@/hooks/use-candidates';
import { isConsentLocked } from '../lib/consent-actions';
import { FileText, Upload } from 'lucide-react';
import { useState } from 'react';
import { CandidateFilePreviewDialog } from './candidate-file-preview-dialog';
import { CandidateFileUploadDialog } from './candidate-file-upload-dialog';
import { CvViewer } from './cv-viewer';

interface CandidateCvPreviewStubProps {
  candidateId: string;
}

export function CandidateCvPreviewStub({ candidateId }: CandidateCvPreviewStubProps) {
  const { data, isLoading } = useCandidateFiles(candidateId);
  // HW-1055: a candidate who revoked consent gets no new files.
  const locked = isConsentLocked(useCandidate(candidateId).data);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const latestCv = data?.CV.find((f) => f.isLatest) ?? null;

  if (isLoading) {
    return (
      <section className="rounded-lg border border-border bg-background p-6 text-sm text-muted-foreground">
        Loading CV…
      </section>
    );
  }

  if (!latestCv) {
    return (
      <>
        <section className="flex flex-col items-center rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
          <FileText className="h-8 w-8 text-muted-foreground/60" aria-hidden />
          <p className="mt-2 text-sm font-medium text-foreground">No CV uploaded yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Upload a PDF to enable inline preview here.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => setUploadOpen(true)}
            disabled={locked}
          >
            <Upload className="mr-1.5 h-3.5 w-3.5" />
            Upload CV
          </Button>
        </section>
        <CandidateFileUploadDialog
          candidateId={candidateId}
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          defaultType="CV"
        />
      </>
    );
  }

  return (
    <>
      <section className="space-y-2 rounded-lg border border-border bg-background p-3">
        <div className="flex items-center justify-between gap-2 px-1">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-foreground">
              {latestCv.filename}
            </div>
            <div className="text-xs text-muted-foreground">
              v{latestCv.version} · latest
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setPreviewOpen(true)}
          >
            Open full CV
          </Button>
        </div>
        {latestCv.isPreviewable ? (
          <CvViewer
            fileUrl={latestCv.url}
            filename={latestCv.filename}
            mimeType={latestCv.mimeType}
            maxPages={1}
            embedded
          />
        ) : (
          <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-center text-xs text-muted-foreground">
            Inline preview is not available for this file type. Download to view.
          </div>
        )}
      </section>

      <CandidateFilePreviewDialog
        file={previewOpen ? latestCv : null}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
      />
    </>
  );
}
